import { Alert, Checkbox } from '@pmi/dsm-react';
import {
  AlertErrorSolidIcon,
  AlertInfoOutlineIcon,
  CloseSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { LockLockedIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { Formik, FormikErrors, FormikProps } from 'formik';
import {
  ComponentProps,
  Dispatch,
  memo,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import Adyen from '../../../../../../shared/components/Adyen';
import PhoneNumberForm from '../../../../../../shared/components/PhoneNumberForm';
import { PhoneNumberInput } from '../../../../../../shared/components/PhoneNumberForm/models';
import RenderHtml from '../../../../../../shared/components/RenderHtml';
import {
  getAdyenClientKey,
  getAdyenEnvironment,
} from '../../../../../../shared/env-vars';
import { AdyenDropinElement } from '../../../../../../shared/models/PaymentModels';
import { useGetSavedPaymentQuery } from '../../apiSlice';
import { SavedPayment } from '../../models';
import BillingAddress, { BillingAddressDetails } from '../BillingAddress';
import styles from '../enhancedEnrollmentModal.module.scss';
import SavedPaymentMethodSelector from './SavedPaymentMethodSelector';

interface AutoRenewPaymentStepProps {
  onCancel: () => void;
  requestAddressInfo: boolean;
  primaryAddress: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  } | null;
  sameAsBillingAddress: boolean;
  onToggleSameAsBillingAddress: (checked: boolean) => void;
  countries: ComponentProps<typeof BillingAddress>['countries'];
  billingAddress: BillingAddressDetails;
  setBillingAddress: Dispatch<SetStateAction<BillingAddressDetails>>;
  validateBillingAddress: (
    values: BillingAddressDetails,
  ) => FormikErrors<BillingAddressDetails>;
  billingAddressFormikRef: MutableRefObject<
    FormikProps<BillingAddressDetails> | undefined
  >;
  adyenDropinRef: MutableRefObject<AdyenDropinElement | null>;
  onAdyenSubmit: NonNullable<ComponentProps<typeof Adyen>['onSubmitCallback']>;
  paymentInlineError: string;
  useRazorpay: boolean;
  shouldRequestRazorpayPhoneNumber: boolean;
  razorpayPhoneCountries: ComponentProps<typeof PhoneNumberForm>['countries'];
  razorpayPhoneInput: PhoneNumberInput;
  setRazorpayPhoneInput: Dispatch<SetStateAction<PhoneNumberInput>>;
  validateRazorpayPhoneNumber: (
    values: PhoneNumberInput,
  ) => FormikErrors<PhoneNumberInput>;
  razorpayPhoneFormikRef: MutableRefObject<
    FormikProps<PhoneNumberInput> | undefined
  >;
  onPaymentChange: (payment: SavedPayment | 'new' | null) => void;
  onPaymentMethodSelected: () => void;
}

function AutoRenewPaymentStep({
  onCancel,
  requestAddressInfo,
  primaryAddress,
  sameAsBillingAddress,
  onToggleSameAsBillingAddress,
  countries,
  billingAddress,
  setBillingAddress,
  validateBillingAddress,
  billingAddressFormikRef,
  adyenDropinRef,
  onAdyenSubmit,
  paymentInlineError,
  useRazorpay,
  shouldRequestRazorpayPhoneNumber,
  razorpayPhoneCountries,
  razorpayPhoneInput,
  setRazorpayPhoneInput,
  validateRazorpayPhoneNumber,
  razorpayPhoneFormikRef,
  onPaymentChange,
  onPaymentMethodSelected,
}: AutoRenewPaymentStepProps) {
  const hasPrimaryAddress = !requestAddressInfo && Boolean(primaryAddress);
  const { data: savedPaymentData, isLoading: isLoadingSavedPayments } =
    useGetSavedPaymentQuery();
  const hasSavedPayments =
    Array.isArray(savedPaymentData) && savedPaymentData.length > 0;
  const [showAddNewPayment, setShowAddNewPayment] = useState(false);
  const [savedPaymentSelected, setSavedPaymentSelected] = useState(false);

  const isAddressDisabled = savedPaymentSelected;

  const handlePaymentChange = (payment: SavedPayment | 'new' | null) => {
    setSavedPaymentSelected(payment !== 'new' && payment !== null);
    onPaymentChange(payment);
  };

  useEffect(() => {
    if (!isLoadingSavedPayments) {
      setShowAddNewPayment(!hasSavedPayments);
      if (!hasSavedPayments) {
        onPaymentChange('new');
      }
    }
  }, [isLoadingSavedPayments, hasSavedPayments, onPaymentChange]);

  const addressSections = (
    <div
      className={isAddressDisabled ? styles.disabledSection : undefined}
      aria-disabled={isAddressDisabled}
    >
      {hasPrimaryAddress && (
        <div className="border border-1 rounded-3 p-3 mb-3">
          <span className="d-block mb-2">
            <strong>Primary Address</strong>
          </span>
          <span className="d-block">{primaryAddress?.address1}</span>
          {primaryAddress?.address2?.length > 0 && (
            <span className="d-block">{primaryAddress.address2}</span>
          )}
          <span className="d-block">
            {primaryAddress?.city}, {primaryAddress?.state}{' '}
            {primaryAddress?.postalCode}
          </span>
        </div>
      )}
      <div className="border border-1 rounded-3 p-3 mb-3">
        <span className="d-block mb-2">
          <strong>Billing Address</strong>
        </span>
        <div className="d-block mb-2">
          <div className="d-block mb-2">
            <Checkbox
              label="Same as primary address"
              value="same-as-primary"
              disabled={isAddressDisabled}
              onChange={(event, checked) => {
                const eventChecked = (event?.target as HTMLInputElement)
                  ?.checked;
                onToggleSameAsBillingAddress(
                  typeof eventChecked === 'boolean' ? eventChecked : !!checked,
                );
              }}
              checked={sameAsBillingAddress}
            />
          </div>
          {!sameAsBillingAddress && (
            <Formik
              initialValues={billingAddress}
              onSubmit={(values) => {
                setBillingAddress(values);
              }}
              validate={validateBillingAddress}
              validateOnBlur={false}
              validateOnChange={false}
              innerRef={billingAddressFormikRef}
              enableReinitialize
            >
              <BillingAddress
                countries={countries}
                setBillingAddress={setBillingAddress}
              />
            </Formik>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.benefitsContainer}>
      <div className="d-flex justify-content-between align-items-center">
        <RenderHtml
          html="Save a payment method for next years renewal"
          tag="span"
          className={styles.customHeader}
        />
        <div>
          <CloseSolidIcon className="cursor-pointer" onClick={onCancel} />
        </div>
      </div>
      <hr className="my-4" />
      <p className={`rounded-3 p-3 mb-3 d-flex gap-2 ${styles.alertAqua}`}>
        <span className="d-block">
          <AlertInfoOutlineIcon fontSize="md" color="info" />
        </span>
        <span>
          <strong>You will not be charged today</strong>
          <br />
          By saving this payment method, you consent to automatic renewal of
          your annual membership. You may turn off auto-renew at any time in
          your myPMI account or by contacting Customer Care.
        </span>
      </p>
      {!isLoadingSavedPayments && addressSections}
      <div>
        {isLoadingSavedPayments && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {!isLoadingSavedPayments && (
          <div
            className={`border border-1 rounded-3 p-3 mb-3${
              paymentInlineError ? ' border-danger' : ''
            }`}
          >
            <div>
              {paymentInlineError && (
                <Alert
                  icon={AlertErrorSolidIcon}
                  variant="danger"
                  alertText={paymentInlineError}
                />
              )}
              <div className={`${styles.ccHeader} mb-2`}>Payment Method</div>
              <div className="d-flex mb-2">
                <LockLockedIcon fontSize="sm" />
                <span className={`${styles.ccHeader} d-inline fw-normal`}>
                  Your privacy is our priority. Your info is always safe with
                  us.
                </span>
              </div>
              {hasSavedPayments && (
                <SavedPaymentMethodSelector
                  onPaymentChange={handlePaymentChange}
                  onPaymentMethodSelected={onPaymentMethodSelected}
                  onShowAddNewPaymentChange={setShowAddNewPayment}
                />
              )}
              {(!hasSavedPayments || showAddNewPayment) &&
                (useRazorpay ? (
                  <>
                    <div
                      className={`${styles.ccBg} p-3 d-flex align-items-center gap-2`}
                    >
                      <span>Secured by</span>
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/8/89/Razorpay_logo.svg"
                        alt="Razorpay logo"
                        width={120}
                        height={24}
                      />
                    </div>
                    {shouldRequestRazorpayPhoneNumber && (
                      <div className="mt-3">
                        <Formik
                          initialValues={razorpayPhoneInput}
                          onSubmit={(values) => {
                            setRazorpayPhoneInput(values);
                          }}
                          validate={validateRazorpayPhoneNumber}
                          validateOnBlur={false}
                          validateOnChange={false}
                          innerRef={razorpayPhoneFormikRef}
                          enableReinitialize
                        >
                          <PhoneNumberForm
                            countries={razorpayPhoneCountries}
                            title=""
                            showExtension={false}
                          />
                        </Formik>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`${styles.ccBg} p-3`}>
                    <Adyen
                      editing={false}
                      id="profile-update-adyen"
                      clientKey={getAdyenClientKey()}
                      environment={getAdyenEnvironment()}
                      handleFormStart={() => {
                        // Reserved for future loading state handling.
                      }}
                      onSubmitCallback={onAdyenSubmit}
                      refs={{ adyenDropinRef }}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(AutoRenewPaymentStep);
